import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Bell } from "lucide-react";
import { BaseModal } from "./BaseModal";
import { SHELF_LIFE_PRESETS } from "../../utils/reminderUtils";

const CUSTOM_PRESET_VALUE = "CUSTOM";

function buildInitialFormState(editingReminder) {
  if (editingReminder) {
    const matchingPreset = SHELF_LIFE_PRESETS.find(
      (preset) => preset.days === editingReminder.durationDays,
    );
    return {
      itemName: editingReminder.itemName,
      madeOrBoughtDate: editingReminder.madeOrBoughtDate,
      preset: matchingPreset ? matchingPreset.days.toString() : CUSTOM_PRESET_VALUE,
      customDays: editingReminder.durationDays.toString(),
    };
  }

  return {
    itemName: "",
    madeOrBoughtDate: format(new Date(), "yyyy-MM-dd"),
    preset: SHELF_LIFE_PRESETS[1].days.toString(),
    customDays: "",
  };
}

export function AddReminderModal({ isOpen, onClose, onSave, editingReminder }) {
  const [form, setForm] = useState(() => buildInitialFormState(editingReminder));
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setForm(buildInitialFormState(editingReminder));
      setError("");
    }
  }, [isOpen, editingReminder]);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleSave = () => {
    if (!form.itemName.trim()) {
      setError("Please enter an item name");
      return;
    }
    if (!form.madeOrBoughtDate) {
      setError("Please enter the date it was made or bought");
      return;
    }

    const durationDays =
      form.preset === CUSTOM_PRESET_VALUE
        ? parseInt(form.customDays, 10)
        : parseInt(form.preset, 10);

    if (!durationDays || durationDays < 1) {
      setError("Please enter how many days it will last");
      return;
    }

    onSave({
      reminderId: editingReminder?.reminderId,
      itemName: form.itemName.trim(),
      madeOrBoughtDate: form.madeOrBoughtDate,
      durationDays,
    });
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={editingReminder ? "Edit reminder" : "Add reminder"}
      titleIcon={<Bell className="w-5 h-5" />}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
          >
            {editingReminder ? "Save changes" : "Add reminder"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="reminder-item-name" className="block text-sm font-medium text-gray-700 mb-2">
            Item name
          </label>
          <input
            id="reminder-item-name"
            type="text"
            value={form.itemName}
            onChange={(e) => handleFieldChange("itemName", e.target.value)}
            placeholder="Dijon mustard"
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="reminder-made-or-bought-date" className="block text-sm font-medium text-gray-700 mb-2">
            Made or bought on
          </label>
          <input
            id="reminder-made-or-bought-date"
            type="date"
            value={form.madeOrBoughtDate}
            max={format(new Date(), "yyyy-MM-dd")}
            onChange={(e) => handleFieldChange("madeOrBoughtDate", e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="reminder-preset" className="block text-sm font-medium text-gray-700 mb-2">
            Lasts about
          </label>
          <select
            id="reminder-preset"
            value={form.preset}
            onChange={(e) => handleFieldChange("preset", e.target.value)}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          >
            {SHELF_LIFE_PRESETS.map((preset) => (
              <option key={preset.days} value={preset.days}>
                {preset.label}
              </option>
            ))}
            <option value={CUSTOM_PRESET_VALUE}>Custom</option>
          </select>
        </div>

        {form.preset === CUSTOM_PRESET_VALUE && (
          <div>
            <label htmlFor="reminder-custom-days" className="block text-sm font-medium text-gray-700 mb-2">
              Number of days
            </label>
            <input
              id="reminder-custom-days"
              type="number"
              min="1"
              value={form.customDays}
              onChange={(e) => handleFieldChange("customDays", e.target.value)}
              placeholder="45"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </BaseModal>
  );
}
